import React, { useEffect, useRef, useState } from 'react';

//components
import MinimisedElementDetails from './MinimisedElementDetails';
import EditElementDetails from './EditElementDetails';

//interfaces
import { IFormElementWidget } from '../../../interfaces/IFormElement';
import BasicFormElementModal from '../../../modals/BasicFormElementModal';
import { useDrag, useDrop } from 'react-dnd';

import '../../../../../styles/formBuilder/elements/form-element-wrapper.scss'

const FormElementWidget = (
    {   
        elementType, 
        labelText, 
        setLabelText, 
        formElementId, 
        isRequiredElement, 
        conditionalCheckboxPanel,
        onSaveClick,
        onDeleteElementClick, 
        onDragElementClick, 
        formElementComponent, 
        isFirstElement, 
        isLastElement, 
        onMoveElementDown, 
        onMoveElementUp,
        isEditModeOpen,
        setIsEditModeOpen,
        rulesComponent,
        advancedOptions,
        validationErrors,
        index
    }: IFormElementWidget ) => {

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const ref = useRef<HTMLDivElement>(null);

    // Drag
    const [{ isDragging }, drag] = useDrag({
      type: 'FormWidget',
      item: { index: index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      end: () => {},
    });

    // Drop
    const [{ isOver }, drop] = useDrop({
      accept: 'FormWidget',
      hover(item: { index: number }, monitor) { 

        if (!ref.current) return;
        const dragIndex = item.index;
        const hoverIndex = index;
        if (dragIndex === hoverIndex) return;

        const hoverRect = ref.current.getBoundingClientRect();
    
        const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset!.y - hoverRect.top;

        // Only trigger move when cursor crosses halfway
        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        onDragElementClick?.(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
      collect: (monitor) => ({
          isOver: monitor.isOver(),
      }),
    });

  // Attach both
  drop(ref);

  // Cursor styling
  useEffect(() => {
    if (isDragging) {
      document.body.classList.add('is-dragging-cursor');
    } else {
      document.body.classList.remove('is-dragging-cursor');
    }
    return () => document.body.classList.remove('is-dragging-cursor');
  }, [isDragging]);

    
    return (
        <div 
            key={`formElement-${formElementId}`} 
            className={`form-element-wrapper ${isDragging &&"is-hovering"}  ${isOver && "is-dragging"}`}
            ref={ref}>
            {isEditModeOpen
                ?   <EditElementDetails 
                    elementType={elementType}
                    labelText={labelText}
                    setLabelText={setLabelText}
                    isRequiredElement={isRequiredElement}
                    formElementId={formElementId} 
                    formElementComponent={formElementComponent} 
                    isFirstElement={isFirstElement} 
                    isLastElement={isLastElement} 
                    onMoveElementUp={onMoveElementUp} 
                    onMoveElementDown={onMoveElementDown}
                    rulesComponent={rulesComponent}
                    onDeleteElementClick={()=>setShowDeleteModal(true)}
                    onSaveClick={onSaveClick}
                    conditionalCheckboxPanel={conditionalCheckboxPanel} 
                    advancedOptions={advancedOptions}
                    validationErrors={validationErrors}
                />
                
                :   <MinimisedElementDetails 
                        elementType={elementType} 
                        labelText={labelText || "INCOMPLETED ELEMENT"} 
                        isRequiredElement={isRequiredElement} 
                        onDeleteElementClick={()=>setShowDeleteModal(true)}    
                        onEditElementClick={()=> setIsEditModeOpen?.()}    
                        dragRef={drag} 
                        validationErrors={validationErrors}         
                    />
            }

            {showDeleteModal &&
                <BasicFormElementModal 
                    headerText={'Delete form field'} 
                    questionText={'Are you sure you want to delete this form field?'} 
                    onCloseClick={()=>setShowDeleteModal(false)} 
                    onConfirmClick={()=>{
                        setShowDeleteModal(false);
                        onDeleteElementClick();
                    }}            
                />
            }
        </div>
    );
};

export default FormElementWidget

